import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from "../../api/axiosInstance";
import {
    DEFAULT_DIVIDEND_OPTION,
    parseLiquidityNeedsDisplay,
    parseLiquidityTimeHorizonDisplay,
} from "../../constants/accountConstants";
import { parseAccountRiskTimeHorizonDisplay } from "../../constants/householdConstants";
import { useToast } from "../../context/ToastContext";
import { toIntOrNull } from "../../utils/validationHelpers";
import Step1GeneralInfo from './Step1GeneralInfo';
import Step2HouseholdSetup from './Step2HouseholdSetup';
import Step3AccountSetup from './Step3AccountSetup';

// Main Wizard Modal Component
const HouseholdSetupWizard = ({ isOpen, onClose, householdId, initialStatus, onComplete, aumAvailability, custodianTypes }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  
  // Track mounted state to avoid set state on unmount
  const isMounted = useRef(false);

  // Initialize and Fetch Data ONCE
  useEffect(() => {
    isMounted.current = true;
    if (isOpen && householdId) {
       // Reset state
       setLoading(true);
       setSaveStatus('idle');
       
       // Determine initial step
       if (initialStatus === 'general_info_completed') {
          setStep(2);
       } else if (initialStatus === 'household_setup_initiated') {
          setStep(3);
       } else {
          setStep(1);
       }

       // Fetch Data
       const fetchData = async () => {
           try {
               const response = await axiosInstance.get(`/households/${householdId}`);
               if (isMounted.current) {
                   setHouseholdData(response.data);
                   setLoading(false);
               }
           } catch (err) {
               console.error("Failed to fetch household for wizard:", err);
               if (isMounted.current) setLoading(false);
           }
       };
       fetchData();
    }
    return () => { isMounted.current = false; };
  }, [isOpen, householdId, initialStatus]);

  // Debounced Auto-Save Function
  const debouncedSave = useCallback(
      debounce(async (id, data) => {
          if (!id || !data) return;
          
          setSaveStatus('saving');
          try {
                  // Helper to parse floats
                  const toFloat = (val) => {
                      if (val === "" || val === null || val === undefined) return null;
                      const num = parseFloat(val);
                      return isNaN(num) ? null : num;
                  };

                  const payload = {
                      // Step 1 Fields
                      is_fee_standard: data.is_fee_standard,
                      custom_fee_percentage: toFloat(data.custom_fee_percentage),
                      
                      // Step 2 Fields
                      estimated_liquid_net_worth: toFloat(data.estimated_liquid_net_worth),
                      estimated_total_net_worth: toFloat(data.estimated_total_net_worth),
                      annual_income: toFloat(data.annual_income),
                      tax_percentage: toFloat(data.tax_percentage),
                      tax_info: data.tax_info, // JSON
                      annual_expense_range: {
                          start: toFloat(data.annual_expense_range?.start),
                          end: toFloat(data.annual_expense_range?.end)
                      },
                      special_expense_range: {
                          start: toFloat(data.special_expense_range?.start),
                          end: toFloat(data.special_expense_range?.end)
                      },
                      investor_associations: data.investor_associations,
                      investor_experience: data.investor_experience,
                      investment_strategy_defaults: data.investment_strategy_defaults,
              };
              
              await axiosInstance.put(`/households/${id}`, payload);
              
              if (isMounted.current) setSaveStatus('saved');
              setTimeout(() => {
                  if (isMounted.current) setSaveStatus('idle');
              }, 2000);

          } catch (err) {
              console.error("Auto-save failed:", err);
              if (isMounted.current) setSaveStatus('error');
          }
      }, 1000), // 1 second debounce
      []
  );

  // Update Data Handler passed to children (with auto-save)
  const updateHouseholdData = (updates) => {
      setHouseholdData(prev => {
          const newData = { ...prev, ...updates };
          // Trigger Auto-Save
          debouncedSave(householdId, newData);
          return newData;
      });
  };

  // Update Data Handler WITHOUT auto-save (for Step 1)
  const updateHouseholdDataLocal = (updates) => {
      setHouseholdData(prev => ({ ...prev, ...updates }));
  };

  // Manual Save Handler for Step 1
  const saveStep1Data = async () => {
      if (!householdId || !householdData) return;
      
      setSaveStatus('saving');
      try {
          const toFloat = (val) => {
              if (val === "" || val === null || val === undefined) return null;
              const num = parseFloat(val);
              return isNaN(num) ? null : num;
          };

          // Build household-level payload
          const householdPayload = {
              is_fee_standard: householdData.is_fee_standard,
              custom_fee_percentage: toFloat(householdData.custom_fee_percentage),
          };

          // Build member payload: personal_info + is_primary + employment_info
          const memberUpdates = (householdData.members || []).map(member => {
              const employment_info = { ...(member.employment_info || {}) };

              // personal_info: carry forward all existing fields (title, names, marital_status, related_to etc.)
              const personal_info = (() => {
                  if (!member.personal_info) return {};
                  if (typeof member.personal_info === 'string') {
                      try { return { ...JSON.parse(member.personal_info) }; } catch { return {}; }
                  }
                  return { ...member.personal_info };
              })();

              const update = {
                  personal_info,
                  is_primary: member.is_primary === true,
              };
              if (Object.keys(employment_info).length > 0) {
                  update.employment_info = employment_info;
              }

              return {
                  member_id: member.id,
                  update,
              };
          });

          // Fire both updates simultaneously in a single logical save operation
          const requests = [
              axiosInstance.put(`/households/${householdId}`, householdPayload),
          ];
          if (memberUpdates.length > 0) {
              requests.push(
                  axiosInstance.put(`/households/${householdId}/members/bulk`, {
                      members: memberUpdates
                  })
              );
          }
          await Promise.all(requests);
          
          if (isMounted.current) setSaveStatus('saved');
          setTimeout(() => {
              if (isMounted.current) setSaveStatus('idle');
          }, 2000);
      } catch (err) {
          console.error("Step 1 save failed:", err);
          if (isMounted.current) setSaveStatus('error');
          throw err; // Re-throw to handle in step navigation
      }
  };

  // Manual Save Handler for Step 2
  const saveStep2Data = async () => {
      if (!householdId || !householdData) return;
      
      setSaveStatus('saving');
      try {
          const toFloat = (val) => {
              if (val === "" || val === null || val === undefined) return null;
              const num = parseFloat(val);
              return isNaN(num) ? null : num;
          };

          const toInt = (val) => {
              if (val === "" || val === null || val === undefined) return 0;
              const num = parseInt(val, 10);
              return isNaN(num) ? 0 : num;
          };

          const normalizeStrategyDefaults = (defaults) => {
              if (!defaults) return defaults;
              const normalized = { ...defaults };
              if (normalized.time_horizon) {
                  normalized.time_horizon = {
                      start: toInt(normalized.time_horizon.start),
                      end: toInt(normalized.time_horizon.end)
                  };
              }
              if (normalized.liquidity_requirements) {
                  const lr = normalized.liquidity_requirements;
                  normalized.liquidity_requirements = {
                      amount: {
                          start: toInt(lr.amount?.start),
                          end: toInt(lr.amount?.end)
                      },
                      time_horizon: {
                          start: toInt(lr.time_horizon?.start),
                          end: toInt(lr.time_horizon?.end)
                      }
                  };
              }
              return normalized;
          };

          // Normalize investor associations: ensure all values are boolean
          const normalizeAssociations = (associations) => {
              if (!associations) return {
                  accredited_investor: false,
                  us_broker_dealer_association: false,
                  broker_dealer_employee: false,
                  ria_association: false,
                  shareholder_or_member_board_director: false,
                  low_priced_securities: false,
              };
              return {
                  accredited_investor: associations.accredited_investor || false,
                  us_broker_dealer_association: associations.us_broker_dealer_association || false,
                  broker_dealer_employee: associations.broker_dealer_employee || false,
                  ria_association: associations.ria_association || false,
                  shareholder_or_member_board_director: associations.shareholder_or_member_board_director || false,
                  low_priced_securities: associations.low_priced_securities || false,
              };
          };

          // Normalize investor experience: keep untouched fields null so they stay pending
          const normalizeExperience = (experience) => {
              if (!experience) return null;
              return {
                  stocks: toIntOrNull(experience.stocks),
                  bonds: toIntOrNull(experience.bonds),
                  mutual_funds: toIntOrNull(experience.mutual_funds),
                  alternative_investments: toIntOrNull(experience.alternative_investments),
                  variable_annuities: toIntOrNull(experience.variable_annuities),
                  partnerships: toIntOrNull(experience.partnerships),
                  options: toIntOrNull(experience.options),
              };
          };

          const payload = {
              // Financial fields
              estimated_liquid_net_worth: toFloat(householdData.estimated_liquid_net_worth),
              estimated_total_net_worth: toFloat(householdData.estimated_total_net_worth),
              annual_income: toFloat(householdData.annual_income),

              // Tax info with tax_bracket as {start, end}
              tax_info: householdData.tax_info,
              
              // Expense ranges
              annual_expense_range: {
                  start: toFloat(householdData.annual_expense_range?.start),
                  end: toFloat(householdData.annual_expense_range?.end)
              },
              special_expense_range: {
                  start: toFloat(householdData.special_expense_range?.start),
                  end: toFloat(householdData.special_expense_range?.end)
              },
              
              // Normalized associations and experience
              investor_associations: normalizeAssociations(householdData.investor_associations),
              investor_experience: normalizeExperience(householdData.investor_experience),
              
              investment_strategy_defaults: normalizeStrategyDefaults(householdData.investment_strategy_defaults),
          };
          
          await axiosInstance.put(`/households/${householdId}`, payload);
          
          if (isMounted.current) setSaveStatus('saved');
          setTimeout(() => {
              if (isMounted.current) setSaveStatus('idle');
          }, 2000);
      } catch (err) {
          console.error("Step 2 save failed:", err);
          if (isMounted.current) setSaveStatus('error');
          throw err; // Re-throw to handle in step navigation
      }
  };
  
  // Specific Handler for Accounts (with auto-save)
  const updateAccountData = async (accountId, accountUpdates) => {
      // 1. Update Local State immediately for UI
      setHouseholdData(prev => ({
          ...prev,
          accounts: prev.accounts.map(acc => acc.id === accountId ? { ...acc, ...accountUpdates } : acc)
      }));
      
      // 2. Trigger explicit save for this account
      try {
          setSaveStatus('saving');
          await axiosInstance.put(`/households/accounts/${accountId}`, accountUpdates);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
           console.error("Account save failed:", err);
           setSaveStatus('error');
      }
  };

  // Local-only Handler for Accounts (Step 3 - no auto-save)
  const updateAccountDataLocal = (accountId, accountUpdates) => {
      setHouseholdData(prev => ({
          ...prev,
          accounts: prev.accounts.map(acc => acc.id === accountId ? { ...acc, ...accountUpdates } : acc)
      }));
  };

  // Manual Save Handler for Step 3 (All Accounts)
  const saveStep3Data = async () => {
      if (!householdId || !householdData) return;
      
      setSaveStatus('saving');
      try {
          const accounts = householdData.accounts || [];
          
          // Helper functions to convert display values to API format
          const parseTimeHorizon = (displayValue) =>
              parseAccountRiskTimeHorizonDisplay(displayValue);

          const parseLiquidityNeeds = (displayValue) => {
              if (!displayValue || displayValue === 'None') return { start: 0, end: 0 };
              if (typeof displayValue === 'string') return parseLiquidityNeedsDisplay(displayValue);
              // If it's already an object, return as-is
              if (typeof displayValue === 'object' && displayValue !== null) return displayValue;
              return null;
          };

          const parseLiquidityTimeHorizon = (displayValue) => {
              if (!displayValue || displayValue === 'None') return { start: 0, end: 0 };
              if (typeof displayValue === 'string') return parseLiquidityTimeHorizonDisplay(displayValue);
              // If it's already an object, return as-is
              if (typeof displayValue === 'object' && displayValue !== null) return displayValue;
              return null;
          };

          const toFloat = (val) => {
              if (val === "" || val === null || val === undefined) return null;
              const num = parseFloat(val);
              return isNaN(num) ? null : num;
          };

          // Transform frontend risk_tolerance format to API format
          const transformToApiFormat = (frontendRisk) => {
              if (!frontendRisk) return null;
              
              const liquidityNeeds = parseLiquidityNeeds(frontendRisk.liquidity_needs);
              const liquidityTimeHorizon = parseLiquidityTimeHorizon(frontendRisk.liquidity_time_horizon);
              
              return {
                  invest_objectives: frontendRisk.primary_objective ? [frontendRisk.primary_objective] : [],
                  risk_tolerance: frontendRisk.risk_tolerance || null,
                  time_horizon: parseTimeHorizon(frontendRisk.time_horizon),
                  decision_making: frontendRisk.account_decision_making || null,
                  source_of_funds: frontendRisk.source_of_funds ? [frontendRisk.source_of_funds] : [],
                  use_of_funds: frontendRisk.primary_use || null,
                  liquidity_requirements: {
                      amount: liquidityNeeds,
                      time_horizon: liquidityTimeHorizon
                  }
              };
          };

          // Process each account
          const accountUpdatePromises = accounts.map(async (account) => {
              const payload = {
                  fee_based_account: account.fee_based_account !== undefined ? account.fee_based_account : true,
                  account_value: toFloat(account.account_value),
                  margin: account.margin !== undefined ? account.margin : false,
                  options: account.options !== undefined ? account.options : false,
                  is_planned: account.is_planned !== undefined ? account.is_planned : true,
                  
                  transfer_info: {
                      ...(account.transfer_info || {}),
                      is_full_transfer: account.transfer_info?.is_full_transfer !== undefined ? account.transfer_info.is_full_transfer : true,
                      transfer_type: account.transfer_info?.transfer_type || "Brokerage or Trust Company"
                  },
                  
                  proceeds: account.proceeds || "Hold in Core Account Investment Vehicle",
                  security_purchases: account.security_purchases || "Hold in Street Name",
                  distribution_income: account.distribution_income || DEFAULT_DIVIDEND_OPTION,
                  
                  deposits: (account.deposits || []).map(dep => ({
                      id: dep.id,
                      amount: toFloat(dep.amount),
                      frequency: dep.frequency || null,
                      start_date: dep.start_date || null,
                      method: dep.method || null
                  })),
                  
                  withdrawals: (account.withdrawals || []).map(w => ({
                      id: w.id,
                      amount: toFloat(w.amount),
                      frequency: w.frequency || null,
                      start_date: w.start_date || null,
                      method: w.method || null,
                      federal_tax_withholding: toFloat(w.federal_tax_withholding),
                      state_tax_withholding: toFloat(w.state_tax_withholding),
                      destination_type: w.destination_type || 'Bank Account',
                      destination_identifier: w.destination_identifier || null
                  }))
              };

              if (account.risk_tolerance) {
                  payload.risk_tolerance = transformToApiFormat(account.risk_tolerance);
              }
              
              return axiosInstance.put(`/households/accounts/${account.id}`, payload);
          });

          await Promise.all(accountUpdatePromises);
          
          if (isMounted.current) setSaveStatus('saved');
          setTimeout(() => {
              if (isMounted.current) setSaveStatus('idle');
          }, 2000);
      } catch (err) {
          console.error("Step 3 save failed:", err);
          if (isMounted.current) setSaveStatus('error');
          throw err;
      }
  };

  // Step Completion Handler
  const handleStepNext = async (currentStep) => {
      setLoading(true); // temporary lock
      try {
          let nextStatus = '';
          if (currentStep === 1) {
              await saveStep1Data();
              nextStatus = 'general_info_completed';
          }
          if (currentStep === 2) {
              await saveStep2Data();
              nextStatus = 'household_setup_initiated';
          }
          if (currentStep === 3) {
              await saveStep3Data();
              await axiosInstance.put(`/households/${householdId}`, { status: 'active' });
              onComplete();
              return;
          }

          if (nextStatus) {
              await axiosInstance.put(`/households/${householdId}`, { status: nextStatus });
              setStep(currentStep + 1);
          }
      } catch (err) {
          console.error("Failed to advance step:", err);
          const detail = err?.response?.data?.detail;
          toast.error(detail || "Error saving progress to next step.");
      } finally {
          setLoading(false);
      }
  };
  
  // Close / Exit Handler
  const handleClose = () => {
      // Force any pending saves to execute immediately
      debouncedSave.flush(); 
      
      onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} aria-hidden="true"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          
            {/* Header / Progress */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4 relative">
                {/* Close Button */}
                <button 
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-emerald-100 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>

                <div className="flex items-center justify-between text-white">
                    <h2 className="text-xl font-bold">Household Info</h2>
                    <div className="flex items-center gap-3 text-xs font-medium mr-12">
                         {saveStatus === 'saving' && <span className="animate-pulse opacity-80">Saving changes</span>}
                         {saveStatus === 'saved' && <span className="text-emerald-100">Saved</span>}
                         {saveStatus === 'error' && <span className="text-red-200">Save Failed</span>}
                    </div>
                </div>
                {/* Progress Bar */}
                <div className="mt-6 flex items-center justify-between relative px-10 pb-2 max-w-3xl mx-auto">
                    {/* Line */}
                    <div className="absolute left-10 right-10 top-1/2 transform -translate-y-1/2 h-0.5 bg-emerald-800/50 -z-0"></div>
                    
                    {[1, 2, 3].map(s => (
                        <div key={s} className="relative z-10 flex flex-col items-center gap-2">
                             <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-4 transition-colors duration-300 ${step >= s ? 'bg-white text-emerald-700 border-white' : 'bg-emerald-800 text-white border-emerald-700'}`}>
                                 {s}
                             </div>
                             <span className={`text-xs font-semibold ${step >= s ? 'text-white' : 'text-emerald-200'}`}>
                                 {s === 1 ? 'General Info' : s === 2 ? 'Household Setup' : 'Account Setup'}
                             </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto bg-gray-50/50">
                {loading || !householdData ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500 gap-4">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-emerald-600 rounded-full animate-spin"></div>
                        <p>Loading household data...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 && (
                            <Step1GeneralInfo 
                                data={householdData} 
                                updateData={updateHouseholdDataLocal}
                                onNext={() => handleStepNext(1)} 
                            />
                        )}
                        {step === 2 && (
                            <Step2HouseholdSetup 
                                data={householdData} 
                                updateData={updateHouseholdDataLocal}
                                onNext={() => handleStepNext(2)} 
                                onBack={() => setStep(1)} 
                            />
                        )}
                        {step === 3 && (
                            <Step3AccountSetup
                                data={householdData}
                                updateAccountData={updateAccountDataLocal}
                                onFinish={() => handleStepNext(3)}
                                onBack={() => setStep(2)}
                                aumAvailability={aumAvailability}
                                custodianTypes={custodianTypes}
                            />
                        )}
                    </>
                )}
            </div>

        </div>
      </div>
    </div>
  );
};

export default HouseholdSetupWizard;
